import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { User, UserProject, SourcingRequest, InviteUsersData } from '../types';
import { API_URL } from '../lib/config';

interface ProjectUser extends User {
  userProject: UserProject;
}

export const useUsers = (projectId: string) => {
  const [users, setUsers] = useState<ProjectUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/api/users/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch users');
      }

      const result = await response.json();
      
      // Transform the data to include user information
      const projectUsers: ProjectUser[] = (result.users || []).map((up: {
        id: string;
        user: string;
        project: string;
        review_level: number;
        created_at: string;
        users: User;
      }) => ({
        ...up.users,
        userProject: {
          id: up.id,
          user: up.user,
          project: up.project,
          review_level: up.review_level,
          created_at: up.created_at
        }
      }));

      setUsers(projectUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const inviteUsers = async (inviteData: InviteUsersData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const requestBody: {
        project_id: string;
        csv_content?: string;
        invite_type?: string;
        emails?: string;
        requirement_text?: string;
      } = {
        project_id: projectId,
      };

      if (inviteData.csv_file) {
        // Read CSV file content
        const csvContent = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(inviteData.csv_file!);
        });
        
        requestBody.csv_content = csvContent;
        requestBody.invite_type = 'csv';
      } else if (inviteData.emails) {
        requestBody.emails = inviteData.emails;
        requestBody.invite_type = 'emails';
      } else if (inviteData.sourcing_requirement) {
        requestBody.requirement_text = inviteData.sourcing_requirement;
        requestBody.invite_type = 'sourcing';
      }

      const response = await fetch(`${API_URL}/api/users/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to invite users';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // If response is not JSON (e.g., HTML error page), try to get text
          try {
            const errorText = await response.text();
            console.error('Non-JSON error response:', errorText);
            errorMessage = `Server error (${response.status}): Unable to parse response`;
          } catch (textError) {
            errorMessage = `Server error (${response.status})`;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Refresh the users list if not a sourcing request
      if (inviteData.csv_file || inviteData.emails) {
        await fetchUsers();
      }

      return result;
    } catch (err) {
      console.error('Error inviting users:', err);
      throw err;
    }
  };

  const fetchSourcingRequests = async (): Promise<SourcingRequest[]> => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/api/users/${projectId}/sourcing`, {
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch sourcing requests');
      }

      const result = await response.json();
      return result.sourcing_requests || [];
    } catch (err) {
      console.error('Error fetching sourcing requests:', err);
      return [];
    }
  };

  const updateReviewLevel = async (userId: string, reviewLevel: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/api/users/${projectId}/review-level`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          review_level: reviewLevel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update review level');
      }

      // Refresh the users list
      await fetchUsers();
    } catch (err) {
      console.error('Error updating review level:', err);
      throw err;
    }
  };

  const removeUser = async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/api/users/${projectId}/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove user');
      }

      // Refresh the users list
      await fetchUsers();
    } catch (err) {
      console.error('Error removing user:', err);
      throw err;
    }
  };

  const addUser = async (email: string, role: 'attempt' | 'review') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/api/users/${projectId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          role,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add user');
      }

      const result = await response.json();
      
      // Refresh the users list
      await fetchUsers();
      
      return result;
    } catch (err) {
      console.error('Error adding user:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [projectId, fetchUsers]);

  return {
    users,
    loading,
    error,
    inviteUsers,
    fetchUsers,
    fetchSourcingRequests,
    updateReviewLevel,
    removeUser,
    addUser,
  };
};